import { Button, Card, CardHeader, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, List } from "@mui/material"
import { AddCircleOutline } from "@mui/icons-material"
import { size, some, filter } from "lodash"
import pluralize from "pluralize"
import PropTypes from "prop-types"
import { QRCodeSVG } from "qrcode.react"
import { Component } from "react"
import Device from "../device"
import ConfirmWithMfa from "../confirm-with-mfa"
import "./style.scss"

export default class Mfa extends Component {
  static propTypes = {
    mfaSecret: PropTypes.string,
    qrCode: PropTypes.string,
    newMfaDevice: PropTypes.func.isRequired,
    verifyMfaDevice: PropTypes.func.isRequired,
    deleteMfaDevice: PropTypes.func.isRequired,
    openDialog: PropTypes.func.isRequired,
    mfaDevices: PropTypes.array.isRequired,
    saveMfaDeviceName: PropTypes.func.isRequired,
    resetMfa: PropTypes.func.isRequired,
    newDeviceName: PropTypes.string,
    setNewDeviceName: PropTypes.func.isRequired,
  }

  constructor(props, ...args) {
    super(props, ...args)

    this.state = {
      mfaCode: "",
      deletingDeviceId: null,
    }
  }

  onChange = event => {
    this.setState({
      [event.target.name]: event.target.value,
    })
  }

  onChangeNewDeviceName = event => {
    const { setNewDeviceName } = this.props
    setNewDeviceName(event.target.value)
  }

  onVerify = () => {
    const { verifyMfaDevice, newDeviceName } = this.props
    const { mfaCode } = this.state
    if (mfaCode) {
      verifyMfaDevice(newDeviceName, mfaCode)
      this.setState({ ...this.state, mfaCode: "" })
    }
  }

  onAddDevice = () => {
    const { mfaDevices, newMfaDevice, openDialog } = this.props

    const verifiedDevices = filter(mfaDevices, device => device.verified)

    if (size(verifiedDevices)) {
      openDialog({
        title: "Add MFA device",
        content: <ConfirmWithMfa
          message="To add a new MFA device, please enter the authentication code of an existing verified MFA device."
          save={this.onConfirmAddDevice}
          saveText="Add device"
        />,
      })
    } else {
      newMfaDevice(null)
    }
  }

  onConfirmAddDevice = (token) => {
    const { newMfaDevice } = this.props
    newMfaDevice(token)
  }

  onDeleteDevice = (deviceId) => {
    const { mfaDevices, deleteMfaDevice, openDialog } = this.props

    // Is the device we are removing the last verified device?
    const verifiedDevices = filter(mfaDevices, device => device.verified)
    const isLastVerified =
      size(verifiedDevices) === 1
      && some(verifiedDevices, device => device.id === deviceId)

    if (isLastVerified) {
      this.setState({ ...this.state, deletingDeviceId: deviceId })

      openDialog({
        title: "Remove MFA device",
        content: <ConfirmWithMfa
          message="To remove the last verified MFA device, please enter the authentication code of your MFA application."
          save={this.onConfirmDeleteDevice}
          saveText="Confirm"
        />,
      })
    } else {
      deleteMfaDevice(deviceId, null)
    }
  }

  onConfirmDeleteDevice = (token) => {
    const { deleteMfaDevice } = this.props
    const { deletingDeviceId } = this.state

    deleteMfaDevice(deletingDeviceId, token)
    this.setState({ ...this.state, deletingDeviceId: null })
  }

  render() {
    const { mfaDevices, saveMfaDeviceName, qrCode, mfaSecret, resetMfa, newDeviceName } = this.props
    const { mfaCode } = this.state
    const nbDevices = size(mfaDevices)

    let deviceListHeader
    if (nbDevices === 0) {
      deviceListHeader = "Add and verify a device to enable MFA"
    } else {
      deviceListHeader = `${nbDevices} registered ${pluralize("device", nbDevices)}`
      if (!some(mfaDevices, device => device.verified)) {
        deviceListHeader += " (none verified)"
      }
    }

    return <div id="mfa-root" className="modules_user-profile_components_mfa">
      <h5 className="font-weight-bold">Multi-Factor Authentication (MFA) Devices</h5>
      <Card className="mfa-devices" variant="outlined">
        <CardHeader
          className="mfa-devices-header"
          classes={{
            action: "mfa-devices-header-actions",
          }}
          action={
            <IconButton aria-label="Add" onClick={this.onAddDevice} size="large">
              <AddCircleOutline />
            </IconButton>
          }
          subheader={deviceListHeader}
        />
        <List className="mfa-device-list">
          {mfaDevices.map(device =>
            <Device {...{ device, saveMfaDeviceName }} deleteMfaDevice={this.onDeleteDevice} key={device.id} />,
          )}
        </List>
      </Card>
      <Dialog container={() => document.getElementById("mfa-root")} open={!!qrCode}>
        <DialogTitle>Verify MFA Device</DialogTitle>
        <Divider />
        <DialogContent>
          {qrCode && <div className="mfa-qr-code-section">
            <span>Scan this QR code : </span>
            <QRCodeSVG
              className="qr-code"
              value={qrCode}
              size={150}
              bgColor="#ffffff"
              fgColor="#000000"
              level="H"
              marginSize={0}
              imageSettings={{
                x: null,
                y: null,
                height: 36,
                width: 36,
              }}
            />
            <span>{`Or enter this secret into your authenticator device : ${mfaSecret} `}</span>
            <div className="code-section">
              <div className="form-group">
                <label htmlFor="newDeviceName">Device name</label>
                <input
                  className="form-control"
                  type="text"
                  id="newDeviceName"
                  name="newDeviceName"
                  value={newDeviceName}
                  maxLength="40"
                  onChange={this.onChangeNewDeviceName}
                />
              </div>
              <div className="form-group">
                <label htmlFor="mfaCode">Enter code</label>
                <input
                  className="form-control"
                  type="text"
                  id="mfaCode"
                  name="mfaCode"
                  value={mfaCode}
                  onChange={this.onChange}
                />
              </div>
            </div>
          </div>}
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={resetMfa}>Cancel</Button>
          <Button color="primary" onClick={this.onVerify} disabled={!mfaCode}>Verify</Button>
        </DialogActions>
      </Dialog>
    </div>
  }
}
